/**
 * Full content from PDF - Troubleshooting (3), Logging (4), API (5)
 */
const C = (title, html) => ({ title, html });

export const content3 = {
  "3-1-1-overview": C(
    "3.1.1 Overview",
    `<p>SEPM provides periodic hotfixes for each major release. These hotfixes address specific issues and improvements and are packaged in ZIP archives. Customers should regularly check the Zendesk support portal for updates and install the latest available patches for their version.</p>`
  ),

  "3-1-2-downloading": C(
    "3.1.2 Downloading Patches and Hotfixes",
    `<p>Please follow the following process to install hotfixes or other corrections of a released SEPM product version:</p>
<ul>
<li>Navigate to the SEPM Zendesk support portal: https://sepmworldwide.zendesk.com/hc/en-us/categories/360002703160-SEPM-Customer</li>
<li>Navigate to the support area for the major SEPM product version, e.g. "Software/Patches for SEPM 2024-02"</li>
<li>Download the zipfile containing the hotfixes, e.g. patches_24_2_0_2024_12_28.zip</li>
<li>The Zipfile will usually contain: patches_sepm_24_2_0_2024_12_28.zip (Patches for Smallworld 4 - sw40\\x_translator\\source), x_translator_hotfixes_24_2_0_2024_12_28.zip (Hotfixes for Smallworld 5 - sw50\\x_translator), installation.txt, readme.txt, and other files as required.</li>
</ul>`
  ),

  "3-1-3-installing-sw5": C(
    "3.1.3 Installing the Hotfixes (Smallworld 5)",
    `<p>Hotfixes for Smallworld 5 are provided as a zipfile using the naming convention: &lt;SEPM Product&gt;_hotfixes_&lt;Version&gt;_&lt;Date when fixes were released&gt;.zip</p>
<p>To install the Smallworld 5 hotfixes you will need to:</p>
<ul>
<li>Remove the existing hotfixes directory in the corresponding layered product</li>
<li>Unzip the zipfile containing the hotfixes in the directory of the corresponding layered product</li>
<li>Restart your Smallworld session to apply the updates.</li>
</ul>`
  ),

  "3-1-4-installing-sw4": C(
    "3.1.4 Installing the Patches (Smallworld 4)",
    `<p>Patches for Smallworld 4 are provided as a ZIP file with the naming convention: Patches_&lt;Version&gt;_&lt;Date when patches were released&gt;.zip</p>
<p>To install the Smallworld 4 patches you will need to:</p>
<ul>
<li>Delete the complete contents of sw40\\x_translator\\source</li>
<li>Unzip the ZIP file containing the patches in sw40\\x_translator\\source</li>
<li>Build a new image</li>
</ul>`
  ),

  "3-2-1-checklist": C(
    "3.2.1 Checklist for Submitting a Ticket",
    `<p>To facilitate efficient support, ensure the following details accompany your ticket:</p>
<ol>
<li><strong>Environment Information:</strong> SEPM version (e.g., 2025-01) and patch level, Smallworld version (e.g., 5.3.5), other relevant version information (e.g. NRM 5.2.10)</li>
<li><strong>Configuration:</strong> Save the configuration used (Menu: Transfer &gt; Save). This allows SEPM support to see all parameters and options used.</li>
<li><strong>Logs:</strong> Provide all logs. DWG format: "Delete temporary files" option disabled to include TFM, TFD, TSM, TSD, params.xml, and command.txt files. In some cases, an additional export to the TFM/TFD format may provide valuable insights.</li>
<li><strong>Format: Text versus Binary:</strong> Prefer DXF over DWG for text-based troubleshooting.</li>
<li><strong>Screenshots:</strong> Include visuals of GIS configuration and settings, DWG/DXF output in AutoCAD or equivalent viewer.</li>
<li><strong>Previous Exports:</strong> If available, provide examples of exports from earlier versions where the issue did not occur.</li>
<li><strong>Create a directory</strong> with a descriptive name and place all relevant files into it. Create a zipfile from the directory and attach it to the ticket (instead of attaching individual files to the ticket).</li>
</ol>
<p><strong>Best Practices:</strong> Upgrade to the latest SEPM version and install latest hotfixes. Ensure that logs and configuration files are complete. Provide clear descriptions of the issue and expected behavior. Following this checklist will significantly enhance the support process, leading to quicker issue resolution.</p>`
  ),

  "3-3-1-misconfigured-csys": C(
    "3.3.1 Misconfigured Coordinate Systems",
    `<p>If you use internal worlds to display data in non-geographical contexts, then the corresponding map should not be associated with a spatial reference system.</p>
<p><strong>Bad:</strong> Schematic map associated with a spatial reference system / Smallworld Source with Coordinate System</p>
<p><strong>Good:</strong> Schematic map associated with no spatial reference system / Smallworld Source with Unit System</p>
<p>The SEPM X-Translator "From Trail" action reads the world and the unit and/or coordinate system from the map owning the trail. If a spatial reference system is defined for data meant to semantically represent schematic data, then the SEPM X-Translator will try to read the data in the given system, which may lead to unexpected results.</p>`
  ),

  "3-4-1-redirecting-logs": C(
    "3.4.1 Redirecting Log Files",
    `<p>Local client drives (e.g., C:\\) are typically visible in the Citrix session as UNC paths such as \\\\Client\\C$. This can cause problems when exporting with the X-Translator if log files are written directly to such a client drive. In these cases, access errors such as are displayed: Access to \\\\client\\c$\\temp\\export_XTR_ERROR_etc.TXT denied</p>
<p>To avoid access problems with client drives, it is recommended to redirect the log files to a server-side directory. This is done using the following settings in x_translator_client_settings.magik:</p>
<ul>
<li>x_translator_settings.is_log_in_target_dir set to FALSE</li>
<li>x_translator_settings.is_log_in_source_dir set to FALSE</li>
<li>x_translator_settings.x_translator_dir set to a server directory</li>
</ul>
<p>With this configuration, all X-Translator logs are generated on the Citrix server, regardless of whether the export destination is a local client drive.</p>`
  ),

  "4-1-1-overview": C(
    "4.1.1 Overview",
    `<p>Starting with release 2024-01, SEPM introduced the ability to classify and customize log messages for better alignment with specific project or customer requirements. This functionality is implemented via the x_log_mapped API, which utilizes the lookup x_base_settings.x_log_map to determine the severity of a log message.</p>`
  ),

  "4-1-2-default-behavior": C(
    "4.1.2 Default Behavior",
    `<p>The x_log_mapped API categorizes messages into one of the following levels:</p>
<ul>
<li><strong>Error:</strong> Indicates a critical issue that prevents successful processing.</li>
<li><strong>Warning:</strong> Highlights potential issues that do not block processing but may require attention.</li>
<li><strong>Info:</strong> Provides informational messages to track progress or minor occurrences.</li>
</ul>
<p>By default, each message ID is assigned a severity level in x_base_settings.x_log_map. For example:</p>
<pre>x_base_settings.x_log_map[:x_not_imported_path_msg] &lt;&lt; :info
x_base_settings.x_log_map[:x_unsupported_dataless_geom_type] &lt;&lt; :error
x_base_settings.x_log_map[:x_truncated_string] &lt;&lt; :warning</pre>`
  ),

  "4-1-3-customizing": C(
    "4.1.3 Customizing Log Message Severity",
    `<p>Administrators can override the default classification of log messages to meet specific requirements. For instance: Reclassify an info message as a warning or error. Suppress a message entirely by mapping it to the value :ignore.</p>
<p>Example - Reclassifying Messages: x_base_settings.x_log_map[:x_no_source_collection] &lt;&lt; :warning, etc.</p>
<p>Example - Suppressing Messages: x_base_settings.x_log_map[:x_no_source_collection] &lt;&lt; :ignore</p>`
  ),

  "4-1-4-implementation": C(
    "4.1.4 Current Implementation",
    `<p>The x_log_mapped API is currently implemented only in the Mapping objects and the Smallworld target format (x_translator_smallworld_target). The following errors are configured by default:</p>
<p><strong>Smallworld Target Format Errors:</strong> :x_not_imported_path_msg (Info), :x_going_to_alternative (Info), :x_empty_target_table (Info), :x_skipping_no_properties (Warning), :x_no_records_for_predicate (Error), :x_unsupported_dataless_geom_type (Error), :x_geometry_lost (Warning), :x_no_repository_entry (Error)</p>
<p><strong>Dataset and Collection Mapping Errors:</strong> :x_no_source_collection, :x_no_target_collection, :x_no_source_and_target_collection, etc. (Info by default)</p>`
  ),

  "4-1-5-use-cases": C(
    "4.1.5 Use Cases & Best Practices",
    `<p>Default Severity of these messages can be considered in the following cases:</p>
<ol>
<li><strong>Highlight Critical Issues:</strong> Reclassify certain messages as errors to ensure they are flagged prominently.</li>
<li><strong>Reduce Noise:</strong> Suppress informational messages that are not relevant to specific workflows.</li>
<li><strong>Adapt to Customer Requirements:</strong> Tailor log classifications to align with customer expectations and SLAs.</li>
</ol>
<p><strong>Best Practices:</strong> Reclassify messages only after careful consideration of their impact on workflows. Avoid suppressing critical messages that indicate data integrity issues. Maintain a version-controlled record of customizations for audit purposes.</p>`
  ),

  "4-2-1-configuration": C(
    "4.2.1 Configuration",
    `<p>By default the log will contain the messages shown below. However in some situations you may prefer to suppress the message. The ignore_warning XML Attribute allows doing that:</p>
<pre>&lt;attribute_mapping attribute1="Name" attribute2="name" ignore_warning="True"/&gt;</pre>
<p>In this case, if either the source or the target do not supply the attribute configured in the mapping, no message will be displayed.</p>`
  ),

  "4-2-2-error-logs": C(
    "4.2.2 Mapping Error Logs",
    `<p>The following table shows a list of common log messages, along with potential causes and resolutions:</p>
<p><strong>Duplicate attribute or geometry mappings:</strong> Resolution - Search for duplicate mappings in the mapping file and remove one of them.</p>
<p><strong>Missing source or target collection:</strong> Resolution - The reason could be a misspelled collection name or the source or target does not supply the mentioned collection.</p>
<p><strong>Invalid attribute or geometry names are used:</strong> Resolution - The reason could be a misspelled attribute or geometry name or the source or target does not supply the mentioned attribute or geometry.</p>
<p><strong>Invalid attribute names in fixed mappings are collection predicates:</strong> Resolution - The reason could be a misspelled attribute name or the source or target does not supply the mentioned attribute.</p>`
  ),
};
